import moment from 'moment';
import { createSelector } from 'reselect';
import {
  currentCourseSelector,
  currentCourseOutcomesSelector,
} from '../Courses/selectors';
import { currentUserEnrolmentsByCourseSelector } from '../Enrolments/selectors';
import { priceFormat } from '../../web/utilities/utilities';
import { selectCurrentCoursePromoData } from '../CoursePromoData/selectors';
import { cohortsSelector } from '../Cohorts/selectors';
import { usersSelector, usersByProfileNameSelector } from '../Users/selectors';
import {
  selectDefaultPortal,
  selectCurrentCourseLinkedPortal,
} from '../Portals/selectors';
import { selectCurrentPortalName } from '../Auth/selectors.web';
import { getCourseCost } from './helpers';

export const portalSelector = (state) => state.Portal;

const currentPortalQualificationSelector = createSelector(
  portalSelector,
  (portal) => {
    if (portal.get('qualification', null) !== null) {
      return portal.get(portal.get('qualification'), null);
    }
    return null;
  }
);

const updateCourseData = (course, defaultCohort = null) => {
  let portalCourse = course;

  const hasCertification =
    portalCourse.getIn(['certification', 'type'], 'no_certificate') !==
    'no_certificate';
  portalCourse = portalCourse.set('hasCertification', hasCertification);

  if (hasCertification) {
    const code = portalCourse.getIn(['certification', 'code'], null);
    if (code) {
      const uocDescription = [
        portalCourse.getIn(['certification', 'code'], null),
        portalCourse.getIn(['certification', 'data', 'name'], null),
      ]
        .filter((v) => v)
        .join(' - ');
      portalCourse = portalCourse.set('uocDescription', uocDescription);
    } else {
      portalCourse = portalCourse.set(
        'certificateType',
        portalCourse.getIn(['certification', 'data', 'name'], null)
      );
    }
  }

  const { enrolmentPrice, certificationPrice } = getCourseCost({
    portalCourse,
    cohort: defaultCohort,
  });

  portalCourse = portalCourse.set('enrolmentPrice', enrolmentPrice);
  portalCourse = portalCourse.set('certificationPrice', certificationPrice);

  portalCourse = portalCourse.set(
    'timeEstimate',
    portalCourse.get('timeCommitment', '') || 'Flexible'
  );

  portalCourse = portalCourse.set(
    'numberOfStudents',
    portalCourse.getIn(['studentSummary', 'totalStudents'], 0)
  );

  return portalCourse;
};

const updateQualificationCourseEnrolments = (qualification, enrolments) => {
  const setEnrolment = (course) => {
    const isEnrolled = enrolments.has(course.get('path'));
    return course.set('isEnrolled', isEnrolled);
  };

  let qualificationData = qualification;
  qualificationData = qualificationData.set(
    'coreCourses',
    qualificationData.get('coreCourses').map(setEnrolment)
  );
  qualificationData = qualificationData.set(
    'electiveCourses',
    qualificationData.get('electiveCourses').map(setEnrolment)
  );

  return qualificationData;
};

const updateQualificationCourseData = (qualification, cohorts) => {
  let qualificationData = qualification;
  qualificationData = qualificationData.set(
    'coreCourses',
    qualificationData
      .get('coreCourses')
      .map((item) =>
        updateCourseData(
          item,
          cohorts && cohorts.get(item.get('defaultCohort'))
        )
      )
  );
  qualificationData = qualificationData.set(
    'electiveCourses',
    qualificationData
      .get('electiveCourses')
      .map((item) =>
        updateCourseData(
          item,
          cohorts && cohorts.get(item.get('defaultCohort'))
        )
      )
  );

  return qualificationData;
};

export const selectCurrentPortal = createSelector(
  portalSelector,
  selectCurrentPortalName,
  selectCurrentCourseLinkedPortal,
  selectDefaultPortal,
  (portal, currentPortalName, linkedPortal, defaultPortal) => {
    let currentPortal = null;
    if (linkedPortal) {
      // uses the linked portal for the course
      currentPortal = linkedPortal;
    } else if (currentPortalName && portal.get(currentPortalName)) {
      // uses the portal from "portal-data" loader
      // such as portal landing page which doesn't have a course.
      currentPortal = portal.get(currentPortalName);
    }
    return currentPortal || defaultPortal;
  }
);

export const selectCurrentPortalCourse = createSelector(
  currentCourseSelector,
  currentCourseOutcomesSelector,
  selectCurrentCoursePromoData,
  cohortsSelector,
  currentUserEnrolmentsByCourseSelector,
  usersSelector,
  usersByProfileNameSelector,
  (
    course,
    outcomes,
    coursePromoData,
    cohorts,
    enrolments,
    users,
    usersByProfileName
  ) => {
    let portalCourse = course;
    if (portalCourse) {
      if (outcomes) {
        // the currentCourseOutcomesSelector filters out any deleted outcomes
        portalCourse = portalCourse.set('outcomes', outcomes);
      }

      portalCourse = portalCourse.set(
        'creator',
        users.get(portalCourse.get('createdBy'))
      );

      const defaultCohort = cohorts.get(portalCourse.get('defaultCohort'));
      if (defaultCohort) {
        [
          'startDate',
          'endDate',
          'paidCohort',
          'studentPrice',
          'currency',
          'requiresAccessCode',
          'isInviteOnly',
        ].forEach((key) => {
          portalCourse = portalCourse.set(key, defaultCohort.get(key));
        });

        const enrolmentCloseDate = defaultCohort.getIn([
          'enrolmentSettings',
          'enrolmentCloseDate',
        ]);
        const closeDate = moment(enrolmentCloseDate);
        const isClosedForEnrolment =
          closeDate.isValid() && closeDate.isBefore();

        const enrolmentEndDate = defaultCohort.get('endDate');
        const endDate = moment(enrolmentEndDate);
        const isClosed = endDate.isValid() && endDate.isBefore();

        portalCourse = portalCourse.set(
          'closedCohort',
          isClosedForEnrolment || isClosed
        );
        portalCourse = portalCourse.set(
          'enrolmentCloseDate',
          enrolmentCloseDate
        );
      }

      if (coursePromoData) {
        // this must be done *after* merging with default cohort data
        // because studentPrice and currency from default cohort can be
        // overwritten by institution specific pricing in course promo data
        portalCourse = portalCourse.merge(coursePromoData);

        portalCourse = portalCourse.update('teamMembers', (teamMembers) => {
          return teamMembers
            .map((profileName) => usersByProfileName.get(profileName))
            .filter((x) => x);
        });
      }

      portalCourse = updateCourseData(portalCourse);

      if (portalCourse.get('qualifications')) {
        portalCourse = portalCourse.update(
          'qualifications',
          (qualifications) => {
            return qualifications.map((qualification) => {
              return updateQualificationCourseData(qualification, cohorts);
            });
          }
        );
      }

      if (enrolments.size) {
        const isEnrolled = enrolments.has(portalCourse.get('path'));
        portalCourse = portalCourse.set('isEnrolled', isEnrolled);
        if (portalCourse.get('qualifications')) {
          portalCourse = portalCourse.update(
            'qualifications',
            (qualifications) => {
              return qualifications.map((qualification) => {
                return updateQualificationCourseEnrolments(
                  qualification,
                  enrolments
                );
              });
            }
          );
        }
      }
    }
    return portalCourse;
  }
);

export const selectCurrentPortalThemeData = createSelector(
  selectCurrentPortal,
  (currentPortal) => {
    let themeData = null;
    if (currentPortal) {
      themeData = currentPortal.get('themeData');
    }
    return themeData ? themeData.toJS() : null;
  }
);

export const portalQualificationSelector = createSelector(
  currentPortalQualificationSelector,
  currentUserEnrolmentsByCourseSelector,
  cohortsSelector,
  (qualification, enrolments, cohorts) => {
    let qualificationData = qualification;

    if (qualificationData) {
      let startDate = 'Start any time';
      const momentDate = moment(qualificationData.get('startDate'));
      if (momentDate.isValid()) {
        startDate = momentDate.format('DD MMM YYYY');
      }
      qualificationData = qualificationData.map((value, key) => {
        if (key.endsWith('Template')) {
          let content = value.get('content');
          content = content.replace(/{startdate}/, startDate);
          content = content.replace(
            /{price}/,
            priceFormat(
              qualificationData.get('price'),
              qualificationData.get('currency')
            )
          );
          return value.set('content', content);
        }
        return value;
      });

      qualificationData = updateQualificationCourseData(
        qualificationData,
        cohorts
      );

      if (enrolments.size) {
        qualificationData = updateQualificationCourseEnrolments(
          qualificationData,
          enrolments
        );
      }
    }

    return qualificationData;
  }
);

export const makeSelectAutoAddNewUsersToPortal = (portalName) =>
  createSelector(portalSelector, (portal) => {
    return portal.getIn([portalName, 'autoAddNewUsersToPortal'], true);
  });

export const selectCurrentPortalCourseCredentialType = createSelector(
  selectCurrentPortalCourse,
  (portalCourse) =>
    portalCourse.getIn(['certification', 'data', 'credentialDisplayName'])
);
